// Pure, DOM-free math for the curated carousel's JS-driven drift.
// The rendered track is two identical copies of one "unit"; keeping the
// offset within [0, unitWidth) makes looping in both directions seamless.

export interface DriftGate {
  pauseUntil: number;   // performance.now() ms until which drift stays paused
  hovered: boolean;
  touchHeld: boolean;
  tabHidden: boolean;
  reducedMotion: boolean;
}

export interface AdvanceInput {
  pos: number;
  pendingNudge: number;
  dtMs: number;
  driftSpeed: number;   // px per second
  driftActive: boolean;
  unitWidth: number;
  easeFraction: number; // 0..1 portion of the pending nudge consumed this frame
  instant: boolean;     // reduced motion: apply the whole nudge in one frame
}

export interface AdvanceResult {
  pos: number;
  pendingNudge: number;
}

export function wrapOffset(pos: number, unitWidth: number): number {
  if (unitWidth <= 0) return 0;
  return ((pos % unitWidth) + unitWidth) % unitWidth;
}

export function accumulateNudge(pending: number, delta: number): number {
  return pending + delta;
}

export function isDriftActive(now: number, state: DriftGate): boolean {
  return (
    now > state.pauseUntil &&
    !state.hovered &&
    !state.touchHeld &&
    !state.tabHidden &&
    !state.reducedMotion
  );
}

export function advance(input: AdvanceInput): AdvanceResult {
  const step = input.driftActive ? (input.driftSpeed * input.dtMs) / 1000 : 0;
  const consumed =
    input.instant || Math.abs(input.pendingNudge) < 0.5
      ? input.pendingNudge
      : input.pendingNudge * input.easeFraction;
  return {
    pos: wrapOffset(input.pos + step + consumed, input.unitWidth),
    pendingNudge: input.pendingNudge - consumed,
  };
}
